'use client'

import React, { useCallback, useEffect, useRef, useState } from 'react'

import {
  LocaleButton,
  LocaleButtonIcon,
  LocaleButtonText,
  SelectMark,
  moveFocus,
  useScrollDirection,
} from '@syconium/little-miss-figgy'

import {
  PrimaryNavLocalizationDialogContent,
  PrimaryNavLocalizationDialogContentFooter,
  PrimaryNavLocalizationDialogHeader,
} from '../../../app/_components/chrome/PrimaryNavLocalization.client'
import {
  NextDialog,
  NextDialogProvider,
  NextDialogTrigger,
} from '../../../app/_components/dialogs/NextDialog.client'
import { useTranslatedLocalization } from '../../../app/_hooks/useTranslatedLocalization'
import { useFixturesContext } from '../../../brunswick/containers/fixtures/FixturesContext'
import { NavContainer } from '../../../brunswick/containers/NavContainer'
import { NavSectionData, NavTab } from '../../../types/graphql/site-fixtures'
import { NavOverlayAccordion } from '../NavOverlayAccordion'
import { useNavSubMenuDialogState } from '../NavSubMenuDialog'
import { PromoBarFromContext } from '../PromoBar'
import { renderNavSection } from '../renderNavSection'
import { usePrimaryNavNestedMenuExperiment } from '../usePrimaryNavNestedMenuExperiment'

import { augmentNavSections } from './augmentNavSections'
import {
  Body,
  Bottom,
  CloseButton,
  Header,
  HelpIcon,
  HelpLink,
  Middle,
  Tab,
  TabList,
  TabPanel,
  Top,
} from './styles'

/*
  Despite what the TabList, Tab, and TabPanel component names might suggest, we do not
  use the ARIA tablist, tab, and tabpanel roles. The ARIA spec dictates behavior we do
  not want, like arrow keys between tabs and tabbing from the selected tab directly to
  the tabpanel, not to the other tab. Native buttons work, no need for an ARIA widget.
*/
export const Template: React.FC<{
  activeTab?: NavTab
  closeButtonLabel: string
  closeModal: () => void
  helpLinkHref?: string
  helpLinkText?: string
  lang?: string
  langModalButton?: () => void
  regionId: string
  regionName: string
  openFirstNavAccordionSection: boolean
  renderAllNavigationTabs: boolean
  setActiveTab: (tab: NavTab) => void
  tabs?: NavTab[]
}> = ({
  activeTab,
  closeButtonLabel,
  closeModal,
  helpLinkHref,
  helpLinkText,
  openFirstNavAccordionSection,
  renderAllNavigationTabs = false,
  setActiveTab,
  tabs,
}) => {
  const { promoBarHeightMobilePixels: headerOffsetTop } = NavContainer.useContainer()
  const scrollingElementRef = useRef<HTMLDivElement | null>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const scrollDirection = useScrollDirection({
    scrollingElement: scrollingElementRef.current ?? null,
  })

  // Nested-menu A/B test: slides this nav back while a sub menu is open over it.
  const { isSubMenuOpen } = useNavSubMenuDialogState()

  // TODO: When removed after experiment, REMOVE USE CLIENT
  const { isNestedMenuVariant } = usePrimaryNavNestedMenuExperiment()

  const fixtures = useFixturesContext()
  const translatedLocalization = useTranslatedLocalization(fixtures)
  const regionName = translatedLocalization.data.region.name
  const languageName = translatedLocalization.data.languageName
  const flagSrc = translatedLocalization.data.region.flagSrc
  const languageCount = translatedLocalization.data.region.languagesArray.length
  const languagePostfix = languageCount > 1 ? ` | ${languageName}` : ''

  useEffect(() => {
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (closeButtonRef?.current) {
      const closeButton = closeButtonRef.current
      closeButton.focus()
    }
  }, [])

  const [scrollTop, setScrollTop] = useState(0)
  useEffect(() => {
    const scrollingElement = scrollingElementRef.current
    if (!scrollingElement) return

    const scrollCallback = (event: Event) => {
      const target = event.target
      if (target && target instanceof HTMLElement) {
        setScrollTop(target.scrollTop)
      }
    }

    scrollingElement.addEventListener('scroll', scrollCallback, { passive: true })
    return () => {
      scrollingElement.removeEventListener('scroll', scrollCallback)
    }
  }, [scrollingElementRef])

  // Hide header when scrolling down
  const headerPosition =
    scrollDirection === 'up' && scrollTop > headerOffsetTop ? 'sticky' : 'relative'

  const sections = augmentNavSections(activeTab?.sections, { openFirstNavAccordionSection })

  // Only render overlay nav menu for accordion sections
  const renderSection = (section: NavSectionData) =>
    isNestedMenuVariant && section.__typename === 'NavAccordion' ? (
      <NavOverlayAccordion key={section.id} closeModal={closeModal} {...section} />
    ) : (
      renderNavSection(section)
    )

  const onBodyKeyDownHandler = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      const shouldMoveToNext = event.key === 'ArrowDown' || event.key === 'ArrowRight'
      const shouldMoveToPrevious = event.key === 'ArrowUp' || event.key === 'ArrowLeft'
      const navigationElement = document.querySelector(Body.toString())

      if (shouldMoveToNext) {
        moveFocus(navigationElement, false, true)
        event.preventDefault()
      } else if (shouldMoveToPrevious) {
        moveFocus(navigationElement, true, true)
        event.preventDefault()
      }
    },
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [moveFocus]
  )

  return (
    <Body
      data-testid='nav-modal-content-body'
      data-nav-pushed-back={isSubMenuOpen}
      onKeyDown={onBodyKeyDownHandler}
      ref={scrollingElementRef}
    >
      <Top>
        <PromoBarFromContext />
        <Header data-testid='nav-modal-content-header' style={{ position: headerPosition }}>
          <TabList role='tablist' aria-label='Navigation tabs'>
            {tabs?.map(tab => {
              const isActive = tab.id === activeTab?.id
              return (
                <Tab
                  isActive={isActive}
                  aria-controls={`ID_TABPANEL-${tab.id}`}
                  aria-selected={isActive}
                  data-testid='nav-modal-content-tab'
                  key={tab.id}
                  role='tab'
                  type='button'
                  tabIndex={isActive ? 0 : -1}
                  id={`ID_TAB-${tab.id}`}
                  onClick={() => {
                    setActiveTab(tab)
                    // Blur on click, combined with :hover { outline: none } simulates a no-outline
                    // style for mouse interactions, while leaving outlines for keyboard navigation
                    if (document.activeElement instanceof HTMLElement) document.activeElement.blur()
                  }}
                  textColor={tab.textColor}
                >
                  {tab.render ? tab.render() : <span>{tab.text}</span>}
                </Tab>
              )
            })}
          </TabList>
          <CloseButton
            aria-expanded={true}
            data-testid='nav-modal-content-close-button'
            onClick={closeModal}
            title={closeButtonLabel}
          >
            <SelectMark height='20px' width='20px' strokeWidth={1} />
          </CloseButton>
        </Header>
        {/*
          Note: `renderAllNavigationTabs === true` is only used by a hidden <NavModalContent>
          purely for SEO purposes - to make all navigation links crawlable and receive
          link equity on them.
        */}
        {renderAllNavigationTabs ? (
          <>
            {tabs?.map(tab => (
              <TabPanel
                key={tab.id}
                tabIndex={0}
                role='tabpanel'
                id={`ID_TABPANEL-${tab.id}`}
                aria-labelledby={`ID_TAB-${tab.id}`}
              >
                {tab.sections.map(section => renderNavSection(section))}
              </TabPanel>
            ))}
          </>
        ) : (
          <TabPanel tabIndex={0} role='tabpanel' id={`ID_TABPANEL`} aria-labelledby={`ID_TAB`}>
            {sections.map(section => renderSection(section))}
          </TabPanel>
        )}
      </Top>
      <Middle />
      <Bottom>
        <section>
          <NextDialogProvider>
            <NextDialogTrigger render={<LocaleButton isColorInverted={false} />}>
              <LocaleButtonIcon alt='' src={flagSrc} />
              <LocaleButtonText>{`${regionName}${languagePostfix}`}</LocaleButtonText>
            </NextDialogTrigger>
            <NextDialog
              analyticsTitle='Localization'
              variant='default'
              unmountOnHide
              header={<PrimaryNavLocalizationDialogHeader />}
              footer={<PrimaryNavLocalizationDialogContentFooter />}
            >
              <PrimaryNavLocalizationDialogContent />
            </NextDialog>
          </NextDialogProvider>
        </section>
        <section>
          {helpLinkHref ? (
            <HelpLink href={helpLinkHref}>
              <HelpIcon />
              {helpLinkText}
            </HelpLink>
          ) : null}
        </section>
      </Bottom>
    </Body>
  )
}
